import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useHistory, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { Formik, Field } from 'formik';
import { TextField } from 'formik-material-ui';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Link
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';

import { Button, Form } from "../../components/Wrappers";
import { useHelperStyles } from '../../styles'
import { useApiHttpState } from '../../context/ApiContext'
import Page from '../../components/Page';

const MIN_PASSWORD_LENGTH = 8;

const errorMessage = (er, fallback) =>
  (er && er.response && er.response.data && er.response.data.message) || fallback;

// Landing page for the link an admin sends from the users screen. The account
// already exists, is enabled and has its roles - the only thing missing is a
// password, which only the invitee ever sets.
const AcceptInvitation = () => {
  const { token } = useParams();
  const { api } = useApiHttpState();
  const history = useHistory();
  const helperClasses = useHelperStyles();

  // 'checking' until the token has been validated, so an expired link shows a
  // clear message instead of a password form that fails on submit.
  const [invitation, setInvitation] = useState({ state: 'checking' });
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const loadInvitation = async () => {
      if (!token) {
        setInvitation({ state: 'invalid', message: 'This invitation link is not valid.' });
        return;
      }
      // This GET also primes the csrf-token cookie the accept PUT needs.
      const [er, response] = await api.get(`/users/invitation/${token}`);
      if (cancelled) return;
      if (er) {
        setInvitation({
          state: 'invalid',
          message: errorMessage(er, 'This invitation link is no longer valid. Ask an administrator to send a new one.'),
        });
        return;
      }
      setInvitation({ state: 'valid', ...response.data });
    };
    loadInvitation();
    return () => { cancelled = true; };
  }, [token, api]);

  return (
    <Page title="Set Your Password">
      <Box
        display="flex"
        flexDirection="column"
        height="100%"
        justifyContent="center"
      >
        {/* No Header here: PublicLayout already renders one above the routed page. */}
        <Container maxWidth="sm">
          <Box mb={3}>
            <Typography color="textPrimary" variant="h2">
              Set Your Password
            </Typography>
          </Box>

          {invitation.state === 'checking' &&
            <Box display="flex" justifyContent="center" my={4}>
              <CircularProgress />
            </Box>
          }

          {invitation.state === 'invalid' &&
            <Alert severity="error" variant="filled" className={helperClasses.marginTopBottom}>
              {invitation.message}
            </Alert>
          }

          {invitation.state === 'valid' && !accepted &&
            <Formik
              initialValues={{ password: '', passwordConfirm: '' }}
              validationSchema={Yup.object().shape({
                password: Yup.string()
                  .min(MIN_PASSWORD_LENGTH, `Password must be at least ${MIN_PASSWORD_LENGTH} characters`)
                  .max(255)
                  .required('Password is required'),
                passwordConfirm: Yup.string()
                  .max(255)
                  .required('Confirm Password is required')
                  .oneOf([Yup.ref('password'), null], 'Passwords must match')
              })}
              onSubmit={async (values, { setSubmitting, setStatus }) => {
                setSubmitting(true);
                setStatus('');
                const [er] = await api.put('/users/invitation/accept', { token, password: values.password });
                if (er) {
                  setStatus(errorMessage(er, 'Your password could not be set. Please try again.'));
                } else {
                  // No auto sign-in: the account is confirmed by logging in with
                  // the password that was just chosen.
                  setAccepted(true);
                }
                setSubmitting(false);
              }}
            >
              {({ isSubmitting, status }) => (
                <Form>
                  <Typography color="textSecondary" variant="body1">
                    {invitation.firstName ? `Welcome, ${invitation.firstName}. ` : ''}
                    Choose a password for <strong>{invitation.email}</strong>, then sign in with it.
                  </Typography>
                  <Field
                    component={TextField}
                    name="password"
                    type="password"
                    label="Password"
                    variant="outlined"
                    margin="normal"
                    fullWidth
                    helperText={`At least ${MIN_PASSWORD_LENGTH} characters`}
                  />
                  <Field
                    component={TextField}
                    name="passwordConfirm"
                    type="password"
                    label="Confirm Password"
                    variant="outlined"
                    margin="normal"
                    fullWidth
                  />
                  <Box my={2}>
                    {!!status &&
                      <Alert severity="error" variant="filled" className={helperClasses.marginTopBottom}>{status}</Alert>
                    }
                    <Button
                      color="primary"
                      disabled={isSubmitting}
                      fullWidth
                      size="large"
                      type="submit"
                      variant="contained"
                    >
                      Set Password
                    </Button>
                  </Box>
                </Form>
              )}
            </Formik>
          }

          {accepted &&
            <Box>
              <Alert severity="success" variant="filled" className={helperClasses.marginTopBottom}>
                <Typography variant="h3">
                  Your password is set. You can now sign in with {invitation.email}.
                </Typography>
              </Alert>
              <Button
                color="primary"
                fullWidth
                size="large"
                variant="contained"
                component={RouterLink}
                to="/login"
              >
                Go to Sign In
              </Button>
            </Box>
          }

          {invitation.state === 'invalid' &&
            <Box mt={2}>
              <Typography variant="body1">
                <Link component={RouterLink} to="/login" variant="h6">Back to sign in</Link>
              </Typography>
            </Box>
          }
        </Container>
      </Box>
    </Page>
  );
};

export default AcceptInvitation;
